import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { loadBookmarks, deleteBookmark, type Bookmark } from '../services/bookmarkStorage';
import { bookmarkNavStore } from '../store/bookmarkNavStore';
import { collectionNavStore } from '../store/collectionNavStore';
import BoardThumbnail from './BoardThumbnail';
import type { Move } from '../types/game';
import './BookmarksPage.css';

type ViewMode = 'list' | 'card';

export default function BookmarksPage() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [filterTag, setFilterTag]     = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('card');

  useEffect(() => {
    loadBookmarks().then(setBookmarks).catch(console.error);
  }, []);

  const allTags = useMemo(() => {
    const tags = new Set(bookmarks.map(b => b.tag).filter(Boolean));
    return Array.from(tags).sort();
  }, [bookmarks]);

  const filtered = useMemo(() => {
    let result = bookmarks;
    if (filterTag) result = result.filter(b => b.tag === filterTag);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(b =>
        b.tag?.toLowerCase().includes(q) ||
        b.comment?.toLowerCase().includes(q) ||
        b.playerBlack?.toLowerCase().includes(q) ||
        b.playerWhite?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [bookmarks, filterTag, filterSearch]);

  const handleRestore = useCallback((_b: Bookmark, idx: number) => {
    collectionNavStore.clear();
    bookmarkNavStore.setContext(filtered, idx);
    window.location.hash = '';
  }, [filtered]);

  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this bookmark?')) return;
    await deleteBookmark(id);
    setBookmarks(prev => prev.filter(b => b.id !== id));
  }, []);

  return (
    <div className="bm-page">
      {/* Header */}
      <div className="bm-header">
        <h1 className="bm-title">Bookmarks</h1>
        <span className="bm-count">{bookmarks.length} saved</span>
      </div>

      {/* Filter bar */}
      <div className="bm-filters">
        <input
          className="bm-filter-input"
          placeholder="🔍 Search…"
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
        />
        {allTags.length > 0 && (
          <div className="bm-tags">
            <button className={`bm-tag${!filterTag ? ' active' : ''}`} onClick={() => setFilterTag('')}>
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                className={`bm-tag${filterTag === tag ? ' active' : ''}`}
                onClick={() => setFilterTag(t => t === tag ? '' : tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
        <div className="bm-filter-row-right">
          <span className="bm-results">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
          <div className="bm-view-toggle">
            <button className={`bm-view-btn${viewMode === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')} title="List view">☰ List</button>
            <button className={`bm-view-btn${viewMode === 'card' ? ' active' : ''}`} onClick={() => setViewMode('card')} title="Card view">⊞ Cards</button>
          </div>
        </div>
      </div>

      {/* Content */}
      {filtered.length === 0 ? (
        <div className="bm-empty">
          {bookmarks.length === 0
            ? 'No bookmarks yet. Click 🔖 on the board to bookmark a position.'
            : 'No bookmarks match the current filter.'}
        </div>
      ) : viewMode === 'list' ? (
        // ── List view ──────────────────────────────────────────────────
        <div className="bm-list">
          {filtered.map((b, idx) => (
            <div key={b.id} className="bm-row" onClick={() => handleRestore(b, idx)}>
              <div className="bm-row-left">
                {b.tag
                  ? <span className="bm-card-tag">{b.tag}</span>
                  : <span className="bm-card-tag no-tag">Untagged</span>
                }
                <span className="bm-card-move">Move {b.moveNumber}</span>
                {b.comment && <span className="bm-row-comment">{b.comment}</span>}
              </div>
              <div className="bm-row-meta">
                <span>⚫ {b.playerBlack} ⚪ {b.playerWhite}</span>
                <span>{b.boardSize}×{b.boardSize}</span>
                <span>{new Date(b.createdAt).toLocaleDateString()}</span>
              </div>
              <button className="bm-del-btn" onClick={e => handleDelete(b.id, e)} title="Delete">×</button>
            </div>
          ))}
        </div>
      ) : (
        // ── Card view ──────────────────────────────────────────────────
        <div className="bm-grid">
          {filtered.map((b, idx) => (
            <div key={b.id} className="bm-card" onClick={() => handleRestore(b, idx)}>
              <div className="bm-card-board">
                <BoardThumbnail
                  moves={b.moves as Move[]}
                  boardSize={b.boardSize}
                  moveNumber={b.moveNumber}
                  handicap={b.handicap ?? 0}
                  size={160}
                />
              </div>
              <div className="bm-card-body">
                <div className="bm-card-top">
                  <div className="bm-card-left">
                    {b.tag
                      ? <span className="bm-card-tag">{b.tag}</span>
                      : <span className="bm-card-tag no-tag">Untagged</span>
                    }
                    <span className="bm-card-move">Move {b.moveNumber}</span>
                  </div>
                  <button className="bm-del-btn" onClick={e => handleDelete(b.id, e)} title="Delete">×</button>
                </div>
                {b.comment && <div className="bm-card-comment">{b.comment}</div>}
                <div className="bm-card-meta">
                  <span>⚫ {b.playerBlack}</span>
                  <span>⚪ {b.playerWhite}</span>
                  <span>{new Date(b.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
